<template>
    <v-main>
        <v-row justify="center" align="center" class="pt-8">
            <v-card class="pa-6" width="60vw">
                <v-card-title class="d-flex justify-center align-center text-h5">
                    ShipStation API Key Manager
                </v-card-title>

                <!-- Key Status Section -->
                <v-card-text v-if="keyStatus.lastModified">
                    <v-row justify="center" class="pb-4">
                        <v-col cols="12" class="text-center">
                            <div class="text-subtitle-1 pb-2">
                                <strong>Last Updated:</strong> {{ formatDate(keyStatus.lastModified) }}
                            </div>
                            <div class="text-subtitle-1 pb-2">
                                <strong>Expires:</strong> {{ formatDate(keyStatus.expirationDate) }}
                            </div>
                            <v-chip :color="statusColor" dark class="mt-2">
                                <v-icon left small>{{ statusIcon }}</v-icon>
                                {{ statusText }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider class="mb-4"></v-divider>

                <!-- Update Form -->
                <v-card-text>
                    <div class="text-subtitle-1 text-center pb-4">
                        Enter new API credentials from ShipStation:<br>
                        <span class="text-caption grey--text">Settings > Account > API Settings</span>
                    </div>
                    <v-form ref="form" v-model="valid">
                        <v-text-field
                            v-model="apiKey"
                            label="API Key"
                            type="password"
                            outlined
                            :rules="fieldRules"
                            :disabled="updating"
                        ></v-text-field>
                        <v-text-field
                            v-model="apiSecret"
                            label="API Secret"
                            type="password"
                            outlined
                            :rules="fieldRules"
                            :disabled="updating"
                        ></v-text-field>
                    </v-form>
                </v-card-text>

                <v-card-actions class="d-flex justify-center">
                    <v-btn
                        color="primary"
                        :loading="updating"
                        :disabled="!valid || updating"
                        @click="updateKey()"
                    >
                        Update API Key
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-row>

        <v-snackbar v-model="snackBar" :timeout="timeout" :color="snackColor">
            {{ snackText }}
        </v-snackbar>
    </v-main>
</template>

<script>
export default {
    beforeMount() {
        document.title = 'Sandbox - ShipStation Key Manager'
        this.$store.dispatch('updateShowWaffle', true)
        this.$store.dispatch('updateNavbarContent', [])
        this.$store.dispatch('updateNavbarTitle', 'ShipStation Key Manager')
        this.getKeyStatus()
    },
    data() {
        return {
            // Form
            valid: false,
            apiKey: '',
            apiSecret: '',
            fieldRules: [
                v => !!v || 'This field is required',
                v => (v && v.trim().length > 0) || 'Cannot be empty or whitespace',
                v => (v && !/\s/.test(v)) || 'Cannot contain whitespace',
            ],
            // Key status
            keyStatus: {
                lastModified: null,
                daysUntilExpiration: null,
                expirationDate: null,
            },
            // Loading states
            loading: false,
            updating: false,
            // Snackbar
            snackBar: false,
            snackText: '',
            snackColor: 'success',
            timeout: 3000,
        }
    },
    computed: {
        statusColor() {
            const days = this.keyStatus.daysUntilExpiration
            if (days === null) return 'grey'
            if (days <= 0) return 'red'
            if (days <= 30) return 'orange'
            return 'green'
        },
        statusIcon() {
            const days = this.keyStatus.daysUntilExpiration
            if (days === null) return 'mdi-help-circle'
            if (days <= 0) return 'mdi-alert-circle'
            if (days <= 30) return 'mdi-alert'
            return 'mdi-check-circle'
        },
        statusText() {
            const days = this.keyStatus.daysUntilExpiration
            if (days === null) return 'Unknown'
            if (days <= 0) return 'OVERDUE - Key rotation needed'
            if (days === 1) return '1 day until rotation due'
            return `${days} days until rotation due`
        },
    },
    methods: {
        async getKeyStatus() {
            try {
                this.loading = true
                const result = await fetch(
                    'https://1qsj72aerg.execute-api.us-east-2.amazonaws.com/prod/ops/shipstation/key-status',
                    {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' }
                    }
                )
                const data = await result.json()
                if (!result.ok) { throw data.message }
                this.keyStatus = data.data
                this.loading = false
            } catch (error) {
                this.loading = false
                this.snack(error, 'error', 3000)
            }
        },
        async updateKey() {
            if (!this.$refs.form.validate()) return
            try {
                this.updating = true
                const result = await fetch(
                    'https://1qsj72aerg.execute-api.us-east-2.amazonaws.com/prod/ops/shipstation/update-key',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            apiKey: this.apiKey,
                            apiSecret: this.apiSecret,
                        })
                    }
                )
                const data = await result.json()
                if (!result.ok) { throw data.message }
                this.updating = false
                this.apiKey = ''
                this.apiSecret = ''
                this.$refs.form.resetValidation()
                this.snack(data.message, 'success', 3000)
                this.getKeyStatus()
            } catch (error) {
                this.updating = false
                this.snack(error, 'error', 3000)
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return 'N/A'
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            })
        },
        snack(text, color, timeout) {
            this.snackBar = true
            this.snackText = text
            this.snackColor = color
            this.timeout = timeout
        },
    },
}
</script>
